// src/types/status.ts
//
// Mirror of MailSendApi/Models/EmailStatuses.cs. These two files must stay in step.
//
// Every value listed here is one the API actually writes. Nothing aspirational:
// this file previously carried "Sent", "TempFailure" and "Rejected", none of which
// any code path produced, while omitting "Accepted" -- the state almost every
// successful email ends up in.
//
// The Record<> types below are deliberate: adding a status to a union without
// giving it a label and tone is a compile error, so the badge map cannot silently
// fall out of date again.

export const SMTP_STATUSES = [
  'Queued',
  'Processing',
  'Sending',
  'Accepted',
  'Failed',
  'Blocked',
] as const;

export type SmtpStatus = (typeof SMTP_STATUSES)[number];

export const DELIVERY_STATUSES = [
  'Pending',
  'InTransit',
  'Retrying',
  'TemporaryFailure',
  'PermanentFailure',
  // Delivered, LikelyDelivered and Cancelled are all written by the API but had
  // drifted out of this mirror, so they rendered as their raw column values.
  'Delivered',
  'LikelyDelivered',
  'Cancelled',
  'Failed',
  'Expired',
  'Bounced',
  'SoftBounce',
  'Unknown',
] as const;

export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

/** Why pre-send validation refused an email. Reported alongside DeliveryStatus. */
export const PRE_SEND_REASON_CODES = [
  'INVALID',
  'BLOCKED',
  'RETRY_LATER',
  'VALIDATION_FAILED',
  'DISPOSABLE',
  'VALIDATION_ERROR',
] as const;

export type PreSendReasonCode = (typeof PRE_SEND_REASON_CODES)[number];

/** Visual weight for a status, independent of which column it came from. */
export type StatusTone = 'neutral' | 'progress' | 'good' | 'warn' | 'bad';

export interface StatusDisplay {
  /** What the operator reads. Not necessarily the stored value. */
  label: string;
  tone: StatusTone;
  /** Shown on hover. Explains the state in the operator's terms. */
  description: string;
}

export const SMTP_STATUS_DISPLAY: Record<SmtpStatus, StatusDisplay> = {
  Queued: {
    label: 'Queued',
    tone: 'neutral',
    description: 'Waiting to be picked up by the queue processor.',
  },
  Processing: {
    label: 'Processing',
    tone: 'progress',
    description: 'Claimed by a batch, not yet handed to the mail server.',
  },
  Sending: {
    label: 'Sending',
    tone: 'progress',
    description: 'Being transmitted to the mail server now.',
  },
  Accepted: {
    label: 'Accepted',
    tone: 'good',
    description: 'The mail server accepted it. Awaiting a delivery verdict.',
  },
  Failed: {
    label: 'Failed',
    tone: 'bad',
    description: 'Transmission failed. May still retry — check the attempt count.',
  },
  Blocked: {
    label: 'Blocked',
    tone: 'warn',
    description: 'Refused by pre-send validation. Never reached the mail server.',
  },
};

export const DELIVERY_STATUS_DISPLAY: Record<DeliveryStatus, StatusDisplay> = {
  Pending: {
    label: 'Pending',
    tone: 'neutral',
    description: 'Not sent yet.',
  },
  InTransit: {
    // Named InTransit in the data, but the mail is not still moving -- the
    // receiving server already has it. "Sent" is what actually happened.
    label: 'Sent',
    tone: 'progress',
    description: 'Handed over and accepted, waiting to see whether it bounces.',
  },
  Retrying: {
    label: 'Retrying',
    tone: 'warn',
    description: 'A send failed and another attempt is scheduled.',
  },
  TemporaryFailure: {
    label: 'Temporary failure',
    tone: 'warn',
    description: 'Failed for a reason that may clear on its own.',
  },
  PermanentFailure: {
    label: 'Permanent failure',
    tone: 'bad',
    description: 'Failed for a reason that will not clear. Will not retry.',
  },
  Failed: {
    label: 'Failed',
    tone: 'bad',
    description: 'Failed and out of retry attempts.',
  },
  Expired: {
    label: 'Expired',
    tone: 'bad',
    description: 'Too old to be worth retrying.',
  },
  Bounced: {
    label: 'Bounced',
    tone: 'bad',
    description: 'The receiving server rejected it after accepting it.',
  },
  SoftBounce: {
    label: 'Soft bounce',
    tone: 'warn',
    description: 'Rejected for a transient reason, such as a full mailbox.',
  },
  Unknown: {
    label: 'Unknown',
    tone: 'neutral',
    description: 'A delivery report arrived but could not be classified.',
  },
  // These three were missing, so they fell through to the raw-value fallback and
  // rendered as "LikelyDelivered" rather than anything a person would write.
  Delivered: {
    label: 'Delivered',
    tone: 'good',
    description: 'A delivery report confirmed it arrived.',
  },
  LikelyDelivered: {
    label: 'Likely delivered',
    tone: 'good',
    description:
      'No bounce arrived inside the delivery window. SMTP gives no positive ' +
      'delivery receipt, so this is an inference rather than confirmation.',
  },
  Cancelled: {
    label: 'Cancelled',
    tone: 'neutral',
    description: 'Withdrawn before it was sent.',
  },
};

/**
 * Rows written before the status vocabulary was closed can still carry the old
 * SCREAMING_CASE values the pre-send validator used to put in DeliveryStatus.
 * Render them rather than dropping them.
 */
export function describeSmtpStatus(status: string): StatusDisplay {
  return (
    SMTP_STATUS_DISPLAY[status as SmtpStatus] ?? {
      label: status,
      tone: 'neutral',
      description: `Unrecognised status "${status}".`,
    }
  );
}

export function describeDeliveryStatus(status: string): StatusDisplay {
  return (
    DELIVERY_STATUS_DISPLAY[status as DeliveryStatus] ?? {
      label: status,
      tone: 'neutral',
      description: `Unrecognised status "${status}".`,
    }
  );
}
